/**
 * Collection page component that displays user's collected posts and worlds.
 * 收藏頁面組件，顯示用戶收藏的貼文和世界觀。
 */
import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import PageLayout from '@/components/layout/PageLayout';
import LeftSidebar from '@/components/layout/LeftSidebar';
import RightSidebar from '@/components/layout/RightSidebar';
import PostCard from '@/features/posts/components/PostCard';
import Axios from '@/shared/api/axios';
import { useTranslation } from 'react-i18next';
import { usePaginatedList } from '@/hooks/usePaginatedList';
import { getContentItemId } from '@/shared/utils/contentItemId';
import StatusModal from '@/components/ui/StatusModal';

function CollectionPage() {
  const { t } = useTranslation();

  // 從 Redux store 獲取用戶資訊
  const idolId = useSelector(state => state.user.idolId);

  // API 非成功狀態碼統一由 StatusModal 顯示（含攔截器附加的 HTTP_xxx）
  const statusModalRef = useRef(null);
  const [currentStatusCode, setCurrentStatusCode] = useState('');

  // 取得收藏列表（單一頁），交由 usePaginatedList 處理分頁狀態與去重 append
  const fetchPage = async (pageNum) => {
    try {
      const res = await Axios.post("/user/get-collect-list", { page: pageNum, limit: 10, idol_id: idolId });
      if (res.data.status_code !== 'SYSTEM_1000') {
        setCurrentStatusCode(res.data.status_code);
        statusModalRef.current?.show();
        return null;
      }
      return { data: res.data.data, total: res.data.total };
    } catch (error) {
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      } else if (!error.httpErrorCode) {
        console.error('Error fetching collection list: ', error);
      }
      return null;
    }
  };

  const { data: collectionInfo, setData: setCollectionInfo, isLoading, reset } = usePaginatedList({
    fetchPage,
    getId: getContentItemId,
    limit: 10,
  });

  // 初始載入
  useEffect(() => {
    if (idolId) {
      reset();
    }
  }, [idolId]);

  // 本地更新/移除單一貼文或世界觀，取代整包 refetch（避免按讚/收藏/留言等高頻互動時列表跳動）
  // updater 回傳 null/undefined 代表移除該筆
  const patchItem = (itemId, updater) => {
    setCollectionInfo(prev => prev.reduce((acc, item) => {
      const currentId = getContentItemId(item);
      if (currentId !== itemId) {
        acc.push(item);
        return acc;
      }
      const next = updater(item);
      if (next) acc.push(next);
      return acc;
    }, []));
  };

  return (
    <PageLayout
      leftSidebar={
        <LeftSidebar currentPage="collection" />
      }
      rightSidebar={<RightSidebar />}
      isLoading={isLoading}
    >
      {/* 收藏列表 */}
      {collectionInfo.length > 0 ? (
        collectionInfo.map((item) => {
          if (item.class === 'post') {
            return (
              <PostCard
                key={`post-${item.post_info.post_id}`}
                data={item}
                contentType="post"
                onItemChange={(updater) => patchItem(getContentItemId(item), updater)}
                removeOnUncollect={true}
                updateList={reset}
                showBadge={true}
              />
            );
          } else if (item.class === 'world') {
            return (
              <PostCard
                key={`world-${item.world_info.world_id}`}
                data={item}
                contentType="world"
                linkToDetail={true}
                onItemChange={(updater) => patchItem(getContentItemId(item), updater)}
                removeOnUncollect={true}
                updateList={reset}
                showBadge={true}
              />
            );
          }
          return null;
        })
      ) : (
        <div className="text-center">{t("no_collection_data")}</div>
      )}
      <StatusModal ref={statusModalRef} statusCode={currentStatusCode} />
    </PageLayout>
  );
}

export default CollectionPage;
